import { useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Custom hook for managing user points
 * Handles points adjustment, history fetching, and related state management
 */
export const usePoints = () => {
  const [pointsHistory, setPointsHistory] = useState([]);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [adjustingPoints, setAdjustingPoints] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsModalUser, setPointsModalUser] = useState(null);
  const [pointsForm, setPointsForm] = useState({
    points: '',
    reason: '',
    type: 'ADMIN_ADJUSTMENT'
  });

  /**
   * Fetch points history for a specific user
   * @param {string} userId - User ID to fetch points history for
   * @returns {Promise<Object>} - Result object with success status and data
   */
  const fetchPointsHistory = async (userId) => {
    if (!userId) {
      console.error('User ID is required to fetch points history');
      return { success: false, error: 'User ID is required' };
    }

    setPointsLoading(true);
    try {
      const response = await fetch(`http://localhost:5000/api/admin/users/${userId}/points`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPointsHistory(data.data.recentTransactions || []);
        return {
          success: true,
          data: {
            transactions: data.data.recentTransactions || [],
            user: data.data.user
          }
        };
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to load points data');
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error fetching points data:', error);
      toast.error('Failed to load points data');
      return { success: false, error };
    } finally {
      setPointsLoading(false);
    }
  };

  /**
   * Adjust user points (add or deduct)
   * @param {string} userId - User ID to adjust points for
   * @param {Object} adjustmentData - Points adjustment data
   * @param {number} adjustmentData.points - Points amount (positive to add, negative to deduct)
   * @param {string} adjustmentData.reason - Reason for adjustment
   * @param {string} adjustmentData.type - Type of adjustment (default: 'ADMIN_ADJUSTMENT')
   * @returns {Promise<Object>} - Result object with success status and new balance
   */
  const adjustPoints = async (userId, adjustmentData) => {
    const { points, reason, type = 'ADMIN_ADJUSTMENT' } = adjustmentData;

    // Validation
    if (!userId) {
      toast.error('User ID is required');
      return { success: false, error: 'User ID is required' };
    }

    if (!points || !reason) {
      toast.error('Points amount and reason are required');
      return { success: false, error: 'Points amount and reason are required' };
    }

    const pointsValue = parseInt(points);
    if (isNaN(pointsValue) || pointsValue === 0) {
      toast.error('Please enter a valid points amount');
      return { success: false, error: 'Invalid points amount' };
    }

    setAdjustingPoints(true);
    try {
      const response = await fetch(`http://localhost:5000/api/admin/users/${userId}/points/adjust`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          points: pointsValue,
          reason,
          type
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Points adjusted successfully');

        // Refresh points history after successful adjustment
        await fetchPointsHistory(userId);

        return {
          success: true,
          data: {
            newBalance: data.data.newBalance,
            transaction: data.data.transaction
          }
        };
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to adjust points');
        return { success: false, error };
      }
    } catch (error) {
      console.error('Error adjusting points:', error);
      toast.error('Failed to adjust points');
      return { success: false, error };
    } finally {
      setAdjustingPoints(false);
    }
  };

  /**
   * Reset points history state
   * Useful when closing modals or switching users
   */
  const resetPointsHistory = () => {
    setPointsHistory([]);
    setPointsLoading(false);
  };

  /**
   * Open points management modal for a user
   * @param {Object} userData - User data object
   */
  const openPointsModal = async (userData) => {
    console.log('openPointsModal called with:', userData);
    setPointsModalUser(userData);
    setShowPointsModal(true);

    // Fetch points history when opening the modal
    if (userData && userData._id) {
      await fetchPointsHistory(userData._id);
    }
  };

  /**
   * Close points management modal
   */
  const closePointsModal = () => {
    setShowPointsModal(false);
    setPointsModalUser(null);
    setPointsForm({
      points: '',
      reason: '',
      type: 'ADMIN_ADJUSTMENT'
    });
    resetPointsHistory();
  };

  /**
   * Update points form data
   * @param {Object} updates - Object containing form field updates
   */
  const updatePointsForm = (updates) => {
    setPointsForm(prev => ({ ...prev, ...updates }));
  };

  /**
   * Adjust user points (wrapper for adjustPoints with current modal user)
   * @param {Event} e - Form submit event
   */
  const adjustUserPoints = async (e) => {
    e.preventDefault();

    if (!pointsModalUser || !pointsModalUser._id) {
      toast.error('No user selected');
      return { success: false };
    }

    const result = await adjustPoints(pointsModalUser._id, pointsForm);

    if (result.success) {
      // Update the modal user's points balance
      setPointsModalUser(prev => ({
        ...prev,
        pointsBalance: result.data.newBalance
      }));

      // Reset form
      setPointsForm({
        points: '',
        reason: '',
        type: 'ADMIN_ADJUSTMENT'
      });
    }

    return result;
  };

  return {
    // State
    pointsHistory,
    pointsLoading,
    adjustingPoints,
    showPointsModal,
    pointsModalUser,
    pointsForm,

    // Functions
    fetchPointsHistory,
    adjustPoints,
    resetPointsHistory,
    openPointsModal,
    closePointsModal,
    updatePointsForm,
    adjustUserPoints,

    // Direct state setters (for advanced use cases)
    setPointsHistory,
    setPointsLoading,
    setAdjustingPoints
  };
};

export default usePoints;
